import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import {
  IconAlertTriangle,
  IconPackage,
  IconPuzzle,
  IconTable,
  IconTool,
  IconUsers,
} from "@tabler/icons-react"
import { useQuery } from "@tanstack/react-query"
import clsx from "clsx"
import { api } from "../../api/client"
import { useStore } from "../../store/useStore"
import { getScopeDisplayName } from "../config/scopeIcons"
const SCOPE_ICONS: Record<string, typeof IconTool> = {
  supplier: IconTool,
  component: IconPuzzle,
  product: IconPackage,
  customer: IconUsers,
  substance: IconAlertTriangle,
}

export function ScopeTabs() {
  const { translate, currentLanguage } = useTranslation()
  const {
    scopes,
    activeScopeId,
    setActiveScopeId,
    hiddenScopes,
    actionState,
    freshnessDate,
  } = useStore()

  const visibleScopes = scopes.filter((s) => !hiddenScopes.has(s.id))
  const inDiffPhase = actionState?.phase === "diff"

  // When an action is active with primary PKs, query filtered record counts per scope
  // to determine which scopes have related records and should be shown
  const actionActive =
    actionState !== null && actionState.primaryPkValues.length > 0
  const primaryScopeId = actionActive ? actionState.primaryScopeId : null
  const primaryPkValues = actionActive ? actionState.primaryPkValues : null
  const visibleScopeIds = visibleScopes.map((s) => s.id)

  const { data: filteredCounts } = useQuery({
    // Every request input is in the key, so unhiding a scope fetches its count
    // instead of reusing a cached set that never included it.
    queryKey: [
      "action-scope-counts",
      primaryScopeId,
      primaryPkValues,
      visibleScopeIds,
    ],
    queryFn: async () => {
      if (primaryScopeId === null || primaryPkValues === null) return null
      const counts: Record<string, number> = {}
      // Query each visible scope for how many records are reachable from primary PKs
      await Promise.all(
        visibleScopeIds.map(async (scopeId) => {
          try {
            const result = await api.listFilteredRecords(
              scopeId,
              primaryScopeId,
              primaryPkValues,
            )
            counts[scopeId] = result.records.length
          } catch {
            counts[scopeId] = 0
          }
        }),
      )
      return counts
    },
    enabled: actionActive,
    staleTime: 10_000, // cache for 10s to avoid re-fetching on every render
  })

  // Determine which scopes to show
  let effectiveScopes =
    actionActive && filteredCounts
      ? visibleScopes.filter((s) => {
          // Always show primary scope
          if (s.id === actionState!.primaryScopeId) return true
          // Show secondary scopes only if they have related records
          return (filteredCounts[s.id] ?? 0) > 0
        })
      : visibleScopes

  // When in diff phase with no freshness filter, hide scopes that have no diff
  if (inDiffPhase && !freshnessDate && actionState) {
    effectiveScopes = effectiveScopes.filter(
      (s) => s.id === actionState.primaryScopeId || !!actionState.diffs[s.id],
    )
  }

  if (effectiveScopes.length === 0) return null

  return (
    <div className="flex items-end gap-0.5 border-b border-grey4 px-4">
      {effectiveScopes.map((scope) => {
        const Icon = SCOPE_ICONS[scope.id] || IconTable
        const isPrimary = actionState?.primaryScopeId === scope.id

        // Show filtered record count for secondary scopes during action (non-diff phase only)
        const filteredCount =
          actionActive && filteredCounts && !isPrimary && !inDiffPhase
            ? (filteredCounts[scope.id] ?? null)
            : null

        // Compute diff progress % during diff phase
        const scopeDiff = inDiffPhase ? actionState!.diffs[scope.id] : null
        let diffPct: number | null = null
        if (scopeDiff) {
          let total = 0,
            resolved = 0
          for (const rec of Object.values(scopeDiff.records)) {
            const cells = Object.values(rec.cells)
            let recTotal = cells.length
            let recResolved = cells.filter((c) => c.accepted !== null).length
            // Row-level freshness counts as one item
            if (rec.rowFreshnessAccepted !== undefined) {
              recTotal += 1
              if (rec.rowFreshnessAccepted !== null) recResolved += 1
            }
            const relDiffs = scopeDiff.relationDiffs?.[rec.recordId]
            if (relDiffs) {
              for (const rd of Object.values(relDiffs)) {
                recTotal += rd.entries.length
                recResolved += rd.entries.filter(
                  (e) => e.accepted !== null,
                ).length
              }
            }
            if (recTotal === 0) {
              recTotal = 1
              recResolved = 1
            }
            total += recTotal
            resolved += recResolved
          }
          diffPct = total > 0 ? Math.round((resolved / total) * 100) : 100
        }

        return (
          <button
            key={scope.id}
            onClick={() => setActiveScopeId(scope.id)}
            className={clsx(
              "flex items-center gap-1.5 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium transition-colors",
              activeScopeId === scope.id
                ? "border-yellow1 text-yellow1"
                : "border-transparent text-grey5 hover:border-grey5 hover:text-white",
            )}
          >
            <Icon size={16} />
            {getScopeDisplayName(scope, currentLanguage)}
            {diffPct !== null && (
              <span
                className={clsx(
                  "text-[10px] tabular-nums",
                  diffPct === 100 ? "text-success" : "text-yellow3",
                )}
              >
                ({diffPct}%)
              </span>
            )}
            {filteredCount !== null && diffPct === null && (
              <span className="ml-1 text-[10px] tabular-nums text-grey5">
                {filteredCount}
              </span>
            )}
            {isPrimary && actionState !== null && (
              <span className="ml-1 rounded bg-yellow1/15 px-1 py-0.5 text-[9px] font-medium text-yellow1">
                {translate("QMS/AUTOPILOT/ANCHOR_BADGE")}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
